import { CameraAlt } from "@mui/icons-material";
import { Alert, Button } from "@mui/material";
import React, { useState, useEffect } from "react";
import "./CameraPermission.css";

const PermissionStatus = {
  PROMPT: "prompt",
  GRANTED: "granted",
  DENIED: "denied",
  UNKNOWN: "unknown",
};

const CameraPermission = ({ onPermissionGranted }) => {
  const [permissionStatus, setPermissionStatus] = useState(
    PermissionStatus.UNKNOWN
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkCameraPermission();
  }, []);

  const checkCameraPermission = async () => {
    setIsLoading(true);
    try {
      // Check if the browser supports the MediaDevices API
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setPermissionStatus(PermissionStatus.UNKNOWN);
        setIsLoading(false);
        return;
      }

      // Check if permission is already granted
      const permissionStatus = await navigator.permissions.query({
        name: "camera",
      });
      setPermissionStatus(permissionStatus.state);

      // If permission is already granted, call the callback
      if (permissionStatus.state === PermissionStatus.GRANTED) {
        onPermissionGranted();
      }

      // Listen for permission changes
      permissionStatus.addEventListener("change", (e) => {
        setPermissionStatus(e.target.state);
        if (e.target.state === PermissionStatus.GRANTED) {
          onPermissionGranted();
        }
      });
    } catch (error) {
      console.error("Error checking camera permission:", error);
      setPermissionStatus(PermissionStatus.UNKNOWN);
    } finally {
      setIsLoading(false);
    }
  };

  const requestCameraPermission = async () => {
    setIsLoading(true);
    try {
      await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setPermissionStatus(PermissionStatus.GRANTED);
      onPermissionGranted();
    } catch (error) {
      console.error("Error requesting camera permission:", error);
      setPermissionStatus(PermissionStatus.DENIED);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  if (permissionStatus === PermissionStatus.GRANTED) {
    return null; // Permission granted, don't show anything
  }

  return (
    <section className="bg-white dark:bg-gray-800 p-4 m-4 rounded-lg shadow space-y-4 relative" style={{ zIndex: 2 }}>
      <div className="permission-content">
        <div className="icon-container">
          {permissionStatus === PermissionStatus.DENIED ? (
            <div className="icon-circle red">
              <CameraAlt className="red-icon" />
            </div>
          ) : (
            <div className="icon-circle blue">
              <CameraAlt className="blue-icon" />
            </div>
          )}
        </div>

        <div className="permission-text">
          <h1 className="text-2xl font-bold">
            {permissionStatus === PermissionStatus.DENIED
              ? "Camera Access Denied"
              : "Camera Access Required"}
          </h1>

          <p className="permission-description">
            {permissionStatus === PermissionStatus.DENIED && (
              <>
                <p className="text-center muted-text">
                  You've denied camera access. Please enable camera access in
                  your browser settings to continue.
                </p>
                <div className="warning-message">
                  <Alert className="warning-icon" />
                  <span>
                    You may need to refresh the page after changing settings.
                  </span>
                </div>
              </>
            )}
            <ul
              style={{
                listStyleType: "square",
                textAlign: "left",
                marginLeft: 20,
              }}
            >
              <li
                className="font-medium text-gray-900 dark:text-gray-100"
                style={{ paddingLeft: 0 }}
              >
                This app requires camera access to activate the light show
                features.
              </li>
              <li
                className="font-medium text-gray-900 dark:text-gray-100"
                style={{ paddingLeft: 0 }}
              >
                After granting access, you can return to this browser page
                anytime during the event to rejoin the light show experience.
              </li>
            </ul>
          </p>

          {permissionStatus !== PermissionStatus.DENIED && (
            <Button onClick={requestCameraPermission} className="allow-button">
              Allow Camera Access
            </Button>
          )}
        </div>
      </div>
    </section>
  );
};

export default CameraPermission;
